import React, { useState } from 'react';
import {
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminLinkAction,
  AdminStatusBadge,
  AdminSupportingText,
} from '../shared/adminUx';
import { getLinkedRecordPath } from '../../../data/moderationQueueSpec';

const FieldRow = ({
  label,
  children,
  copyValue,
  copyKey,
  copiedKey,
  onCopy,
  copyLabel,
}) => {
  const canCopy = Boolean(copyValue && copyValue !== '—');
  const value = children ?? '—';

  return (
    <div className="space-y-1">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      {canCopy ? (
        <div className="flex items-center gap-2 min-w-0">
          <AdminDetailFieldValue as="div" className="truncate min-w-0">
            {value}
          </AdminDetailFieldValue>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onCopy(copyKey, copyValue);
            }}
            className="inline-flex items-center justify-center shrink-0 text-mediumgray hover:text-primaryblue"
            title={copiedKey === copyKey ? 'Copied' : `Copy ${copyLabel || label}`}
            aria-label={copiedKey === copyKey ? 'Copied' : `Copy ${copyLabel || label}`}
          >
            <i
              className={`fi ${
                copiedKey === copyKey ? 'fi-rr-check text-freshgreen' : 'fi-rr-copy'
              } text-xs leading-none`}
              aria-hidden
            />
          </button>
        </div>
      ) : (
        <AdminDetailFieldValue>{value}</AdminDetailFieldValue>
      )}
    </div>
  );
};

/**
 * Overview layout: semantic columns (4 or 5), space-y-4 within each, gap-8 between columns.
 */
const OverviewColumns = ({ columns }) => {
  const count = columns.length;
  const gridClass =
    count >= 5
      ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8'
      : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8';

  return (
    <div className={gridClass}>
      {columns.map((col, index) => (
        <div key={col.key || index} className="space-y-4">
          {col.fields}
        </div>
      ))}
    </div>
  );
};

const display = (value) => (value != null && value !== '' ? value : '—');

const ModerationOverviewTab = ({ model, assignment, variant = 'queue' }) => {
  const [copiedKey, setCopiedKey] = useState(null);

  const handleCopyField = async (fieldKey, value) => {
    if (!value || value === '—') return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopiedKey(fieldKey);
      window.setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      // ignore clipboard errors
    }
  };

  if (!model) {
    return (
      <AdminSupportingText className="text-mediumgray">
        No case overview data available.
      </AdminSupportingText>
    );
  }

  const caseStatus = assignment?.caseStatus || model.status || 'New';
  const supportTeam =
    variant === 'archive'
      ? assignment?.assignedHandler ||
        model.finalisedBy ||
        model.supportTeam ||
        model.assignedTo ||
        'Unassigned'
      : assignment?.assignedHandler || model.supportTeam || model.assignedTo || 'Unassigned';
  const account = model.accountKind === 'Customer' ? model.customerAccount : model.businessAccount;
  const flaggedBy = model.flaggedBy || {};
  const linked = model.linked || {};
  const decisionId =
    variant === 'archive' ? model.moderationDecisionId : linked.moderationDecisionId;
  const sourceRecordPath = getLinkedRecordPath(linked.sourceRecordId);
  const sourceFlagPath =
    getLinkedRecordPath(linked.sourceFlagId, 'flag') || sourceRecordPath;

  const copyProps = (key, value, label) => ({
    copyKey: key,
    copyValue: value && value !== '—' ? value : null,
    copiedKey,
    onCopy: handleCopyField,
    copyLabel: label,
  });

  const idValue = (value, path) => {
    if (!value || value === '—') return '—';
    if (path) return <AdminLinkAction to={path}>{value}</AdminLinkAction>;
    return value;
  };

  // Column 1 — Case (IDs first, then attributes, then timestamps)
  const caseFields = [
    <FieldRow
      key="caseId"
      label="Moderation Case ID"
      {...copyProps('caseId', model.caseId, 'Moderation Case ID')}
    >
      {idValue(model.caseId, getLinkedRecordPath(model.caseId, 'case'))}
    </FieldRow>,
    <FieldRow key="source" label="Source">{display(model.source)}</FieldRow>,
    <FieldRow key="type" label="Type">{display(model.type)}</FieldRow>,
    <FieldRow key="caseReason" label="Case Reason">{display(model.caseReason)}</FieldRow>,
    <FieldRow key="severity" label="Severity">{display(model.severity)}</FieldRow>,
    <FieldRow key="caseStatus" label="Status">
      {caseStatus && caseStatus !== '—' ? (
        <AdminStatusBadge status={caseStatus} label={caseStatus} />
      ) : (
        '—'
      )}
    </FieldRow>,
    <FieldRow key="supportTeam" label="Support Team">{display(supportTeam)}</FieldRow>,
    <FieldRow key="received" label="Received">{display(model.received)}</FieldRow>,
    <FieldRow key="updated" label={variant === 'archive' ? 'Finalised On' : 'Last Updated'}>
      {display(variant === 'archive' ? model.finalisedOn : model.lastUpdated)}
    </FieldRow>,
  ];

  // Column 2 — Account (ID before name)
  const accountFields =
    account?.kind === 'Customer'
      ? [
          <FieldRow
            key="customerId"
            label="Customer ID"
            {...copyProps('customerId', account.customerId, 'Customer ID')}
          >
            {idValue(account.customerId, getLinkedRecordPath(account.customerId))}
          </FieldRow>,
          <FieldRow key="customerName" label="Customer Name">{display(account.customerName)}</FieldRow>,
          <FieldRow key="customerAccountType" label="Account Type">{display(account.accountType)}</FieldRow>,
          <FieldRow key="customerAccountStatus" label="Account Status">{display(account.accountStatus)}</FieldRow>,
          <FieldRow key="customerEmail" label="Email">{display(account.email)}</FieldRow>,
          <FieldRow key="customerPhone" label="Phone">{display(account.phone)}</FieldRow>,
        ]
      : [
          <FieldRow
            key="businessId"
            label="Business ID"
            {...copyProps('businessId', account?.businessId, 'Business ID')}
          >
            {idValue(account?.businessId, getLinkedRecordPath(account?.businessId))}
          </FieldRow>,
          <FieldRow key="businessName" label="Business Name">{display(account?.businessName)}</FieldRow>,
          <FieldRow key="abn" label="ABN">{display(account?.abn)}</FieldRow>,
          <FieldRow key="legalName" label="Account Holder Legal Name">
            {display(account?.accountHolderLegalName)}
          </FieldRow>,
          <FieldRow key="tradeCategory" label="Trade Category">{display(account?.tradeCategory)}</FieldRow>,
          <FieldRow key="businessAccountType" label="Account Type">{display(account?.accountType)}</FieldRow>,
          <FieldRow key="businessAccountStatus" label="Account Status">{display(account?.accountStatus)}</FieldRow>,
        ];

  // Column 3 — Flagged By (IDs before names / descriptive fields)
  let flaggedByFields = [];
  if (flaggedBy.kind === 'System') {
    flaggedByFields = [
      <FieldRow
        key="ccRuleId"
        label="Content Control Rule ID"
        {...copyProps('ccRuleId', flaggedBy.contentControlRuleId, 'Content Control Rule ID')}
      >
        {idValue(flaggedBy.contentControlRuleId, getLinkedRecordPath(flaggedBy.contentControlRuleId))}
      </FieldRow>,
      <FieldRow key="contentIssue" label="Content Issue">{display(flaggedBy.contentIssue)}</FieldRow>,
      <FieldRow key="matchClassification" label="Match Classification">
        {display(flaggedBy.matchClassification)}
      </FieldRow>,
      <FieldRow key="checkContext" label="Check Context">{display(flaggedBy.checkContext)}</FieldRow>,
      <FieldRow key="flaggedOn" label="Flagged On">{display(flaggedBy.flaggedOn)}</FieldRow>,
    ];
  } else if (flaggedBy.kind === 'Business') {
    flaggedByFields = [
      <FieldRow
        key="flagBusinessId"
        label="Business ID"
        {...copyProps('flagBusinessId', flaggedBy.businessId, 'Business ID')}
      >
        {idValue(flaggedBy.businessId, getLinkedRecordPath(flaggedBy.businessId))}
      </FieldRow>,
      <FieldRow key="flagBusinessName" label="Business Name">{display(flaggedBy.businessName)}</FieldRow>,
      <FieldRow key="flagReason" label="Flag Reason">{display(flaggedBy.flagReason)}</FieldRow>,
      <FieldRow key="additionalDetails" label="Additional Details">
        {display(flaggedBy.additionalDetails)}
      </FieldRow>,
      ...(flaggedBy.blockSelected && flaggedBy.blockSelected !== '—'
        ? [
            <FieldRow key="blockSelected" label="Block Selected">
              {flaggedBy.blockSelected}
            </FieldRow>,
          ]
        : []),
      <FieldRow key="flaggedOn" label="Flagged On">{display(flaggedBy.flaggedOn)}</FieldRow>,
    ];
  } else {
    flaggedByFields = [
      <FieldRow
        key="flagCustomerId"
        label="Customer ID"
        {...copyProps('flagCustomerId', flaggedBy.customerId, 'Customer ID')}
      >
        {idValue(flaggedBy.customerId, getLinkedRecordPath(flaggedBy.customerId))}
      </FieldRow>,
      <FieldRow key="flagCustomerName" label="Customer Name">{display(flaggedBy.customerName)}</FieldRow>,
      <FieldRow key="flagReason" label="Flag Reason">{display(flaggedBy.flagReason)}</FieldRow>,
      <FieldRow key="additionalDetails" label="Additional Details">
        {display(flaggedBy.additionalDetails)}
      </FieldRow>,
      ...(flaggedBy.blockSelected && flaggedBy.blockSelected !== '—'
        ? [
            <FieldRow key="blockSelected" label="Block Selected">
              {flaggedBy.blockSelected}
            </FieldRow>,
          ]
        : []),
      <FieldRow key="flaggedOn" label="Flagged On">{display(flaggedBy.flaggedOn)}</FieldRow>,
    ];
  }

  // Column 4 — Linked Cases & References (IDs first, then non-ID references)
  const linkedFields = [
    <FieldRow
      key="sourceFlagId"
      label="Source Flag ID"
      {...copyProps('sourceFlagId', linked.sourceFlagId, 'Source Flag ID')}
    >
      {idValue(linked.sourceFlagId, sourceFlagPath)}
    </FieldRow>,
    <FieldRow
      key="sourceRecordId"
      label="Source Record ID"
      {...copyProps('sourceRecordId', linked.sourceRecordId, 'Source Record ID')}
    >
      {idValue(linked.sourceRecordId, sourceRecordPath)}
    </FieldRow>,
    <FieldRow
      key="moderationDecisionId"
      label="Moderation Decision ID"
      {...copyProps('moderationDecisionId', decisionId, 'Moderation Decision ID')}
    >
      {idValue(
        decisionId,
        getLinkedRecordPath(decisionId) ||
          (decisionId && decisionId !== '—' ? getLinkedRecordPath(model.caseId, 'case') : null)
      )}
    </FieldRow>,
    <FieldRow
      key="previousCaseId"
      label="Previous Moderation Case ID"
      {...copyProps('previousCaseId', linked.previousModerationCaseId, 'Previous Moderation Case ID')}
    >
      {idValue(linked.previousModerationCaseId, getLinkedRecordPath(linked.previousModerationCaseId, 'case'))}
    </FieldRow>,
    <FieldRow
      key="relatedAppealId"
      label="Related Appeal ID"
      {...copyProps('relatedAppealId', linked.relatedAppealId, 'Related Appeal ID')}
    >
      {idValue(linked.relatedAppealId, getLinkedRecordPath(linked.relatedAppealId))}
    </FieldRow>,
    <FieldRow
      key="relatedTicketId"
      label="Related Support Ticket ID"
      {...copyProps('relatedTicketId', linked.relatedSupportTicketId, 'Related Support Ticket ID')}
    >
      {idValue(linked.relatedSupportTicketId, getLinkedRecordPath(linked.relatedSupportTicketId))}
    </FieldRow>,
    <FieldRow
      key="fraudCaseId"
      label="Fraud & Security Case ID"
      {...copyProps('fraudCaseId', linked.fraudSecurityCaseId, 'Fraud & Security Case ID')}
    >
      {idValue(linked.fraudSecurityCaseId, getLinkedRecordPath(linked.fraudSecurityCaseId))}
    </FieldRow>,
    <FieldRow
      key="fraudDecisionId"
      label="Fraud & Security Decision ID"
      {...copyProps('fraudDecisionId', linked.fraudSecurityDecisionId, 'Fraud & Security Decision ID')}
    >
      {idValue(linked.fraudSecurityDecisionId, getLinkedRecordPath(linked.fraudSecurityDecisionId))}
    </FieldRow>,
    <FieldRow key="snapshotRef" label="Captured Snapshot Reference">
      {display(linked.capturedSnapshotReference)}
    </FieldRow>,
    <FieldRow key="flaggedItemRefs" label="Exact Flagged Item Reference(s)">
      {display(linked.exactFlaggedItemRefs)}
    </FieldRow>,
    <FieldRow
      key="warningHistory"
      label="Warning & Restriction History"
      {...copyProps('warningHistory', linked.warningRestrictionHistory, 'Warning & Restriction History')}
    >
      {idValue(linked.warningRestrictionHistory, getLinkedRecordPath(linked.warningRestrictionHistory))}
    </FieldRow>,
  ];

  const columns = [
    { key: 'case', fields: caseFields },
    { key: 'account', fields: accountFields },
    { key: 'flaggedBy', fields: flaggedByFields },
    { key: 'linked', fields: linkedFields },
  ];

  return (
    <div>
      <OverviewColumns columns={columns} />
    </div>
  );
};

export default ModerationOverviewTab;
